import { ConversionMethod } from '../models/deviceState'
import { SensorPort, SensorType, BaseUnits } from '../common/enums'
import { convertToFloat32 } from '../common/utils'

export interface SensorConfig1Message {
  flagSensorEnabled: boolean
  conversionMethod: ConversionMethod
  sensorNumber: number
  sensorPort: SensorPort
  sensorType: SensorType
  sensorUnits: BaseUnits
  conversionScale: number
  conversionOffset: number
}

export function decodeSensorConfig1(data: Uint8Array): SensorConfig1Message {
  const sensorFlags = data[0]
  const conversionMethod = determineConversionMethod((sensorFlags >> 4) & 0x7)
  const sensorNumber = (data[0] & 0x0f) + 1
  const sensorPort = determineSensorPort(sensorNumber)
  const sensorType = determineSensorType(data[1], data[2])
  const sensorUnits = determineUnits(sensorType)
  const conversionScale = convertToFloat32(data.slice(3, 7))
  const conversionOffset = convertToFloat32(data.slice(7, 11))
  const flagSensorEnabled =
    sensorType === SensorType.Unknown ? false : !!(data[0] & 0x80)

  return {
    flagSensorEnabled,
    conversionMethod,
    sensorNumber,
    sensorType,
    sensorPort,
    sensorUnits,
    conversionScale,
    conversionOffset,
  }
}

function determineSensorType(
  sensorType: number,
  sensorSubType: number
): SensorType {
  if (sensorType > 0 && sensorType <= 6) {
    const lookup: { [key: string]: { [key: string]: SensorType } } = {
      1: {
        1: SensorType.VegetronixVH400,
        2: SensorType.Dwyer628,
        3: SensorType.AgriDrain800770,
        4: SensorType.WindDirection,
        5: SensorType.SolarRadiation,
      },
      2: {
        1: SensorType.DEMPaddleWheel,
        2: SensorType.Badgermeter220BR,
        3: SensorType.WindSpeed,
      },
      3: {
        1: SensorType.IrrometerWM200SS,
      },
      4: {
        1: SensorType.SwitchNormallyOpen,
        2: SensorType.SwitchNormallyClosed,
      },
      5: {
        1: SensorType.BadgermeterSDI,
        2: SensorType.Rainfall,
        3: SensorType.CountButtonCB100,
        4: SensorType.ServiceButtonSB100,
        5: SensorType.TiltSensorTS100,
      },
      6: {
        1: SensorType.Pressure,
        2: SensorType.Temperature,
        3: SensorType.Humidity,
        4: SensorType.AquaTroll500DissolvedOxygenSensor,
        5: SensorType.AquaTroll500TurbiditySensor,
        6: SensorType.AquaTroll500TotalDissolvedSolidsSensor,
        7: SensorType.AquaTroll500NitrateSensor,
        8: SensorType.AquaTroll500SpecificConductivitySensor,
        9: SensorType.AquaTroll500SalinitySensor,
        10: SensorType.AquaTroll500TemperatureSensor,
      },
    }

    const sensor = lookup[sensorType][sensorSubType] || SensorType.Unknown

    if (sensor === SensorType.Unknown) {
      console.log(`Unknown: Type=${sensorType}, SubType=${sensorSubType}`)
    }

    return sensor
  }

  return SensorType.None
}

function determineUnits(sensorType: SensorType): BaseUnits {
  //
  return (
    {
      [SensorType.VegetronixVH400]: BaseUnits.VolumetricWaterContent,
      [SensorType.Dwyer628]: BaseUnits.Pressure, // ???
      [SensorType.AgriDrain800770]: BaseUnits.DepthHeightDistance, // ???
      [SensorType.WindDirection]: BaseUnits.WindDirection, // ???
      [SensorType.SolarRadiation]: BaseUnits.SolarRadiation,
      [SensorType.DEMPaddleWheel]: BaseUnits.VolumeRate, // ???
      [SensorType.Badgermeter220BR]: BaseUnits.VolumeRate, // ???
      [SensorType.WindSpeed]: BaseUnits.WindSpeed,
      [SensorType.IrrometerWM200SS]: BaseUnits.Pressure,
      [SensorType.SwitchNormallyOpen]: BaseUnits.SwitchContact,
      [SensorType.SwitchNormallyClosed]: BaseUnits.SwitchContact,
      [SensorType.BadgermeterSDI]: BaseUnits.Volume,
      [SensorType.Rainfall]: BaseUnits.Rainfall,
      [SensorType.Pressure]: BaseUnits.Pressure,
      [SensorType.Temperature]: BaseUnits.Temperature,
      [SensorType.Battery]: BaseUnits.Battery,
      [SensorType.Humidity]: BaseUnits.Humidity,

      [SensorType.AquaTroll500DissolvedOxygenSensor]: BaseUnits.DissolvedOxygen,
      [SensorType.AquaTroll500TurbiditySensor]: BaseUnits.Turbidity,
      [SensorType.AquaTroll500TotalDissolvedSolidsSensor]:
        BaseUnits.TotalDissolvedSolids,
      [SensorType.AquaTroll500NitrateSensor]: BaseUnits.Nitrates,
      [SensorType.AquaTroll500SpecificConductivitySensor]:
        BaseUnits.SpecificConductivity,
      [SensorType.AquaTroll500SalinitySensor]: BaseUnits.Salinity,
      [SensorType.AquaTroll500TemperatureSensor]: BaseUnits.Temperature,
      [SensorType.CountButtonCB100]: BaseUnits.Count,
      [SensorType.ServiceButtonSB100]: BaseUnits.Count,
      [SensorType.TiltSensorTS100]: BaseUnits.Count,

      [SensorType.Unknown]: BaseUnits.Unknown,
      [SensorType.None]: BaseUnits.Unknown,
    }[sensorType] || BaseUnits.Unknown
  )

  // return UnitCategory.Unknown
}

function determineConversionMethod(value: number): ConversionMethod {
  return (
    {
      0: ConversionMethod.None,
      1: ConversionMethod.Method1,
      2: ConversionMethod.Method2,
      3: ConversionMethod.Method3,
      4: ConversionMethod.Method4,
    }[value] || ConversionMethod.None
  )
}

function determineSensorPort(value: number): SensorPort {
  return (
    {
      1: SensorPort.A1,
      2: SensorPort.A2,
      3: SensorPort.B1,
      4: SensorPort.B2,
      5: SensorPort.C1,
      6: SensorPort.C2,
      7: SensorPort.C3,
    }[value] || SensorPort.Unknown
  )
}
